// dependencies
import { fromJS } from "immutable";
import moment from "moment";

// actions
// utils
import Camelfy from "../utils/Camelfy";
import Momentize from "../utils/Momentize";
import SerializeObject from "../utils/SerializeObject";
import DirectoryAlphaCounts from "./utils/Users/DirectoryAlphaCounts";

// constants
import UserObjectRecord from "../constants/Records/Users.userObject";

const baseAction = "USERS";

export function SearchUsers(
	{ query = "", fieldIds = [], letter = "", page = 1, limitSelected = false, lastName = true, ...otherProps },
	userSelectionObjectId,
	exclusionList,
	usersToExcludeSelectionId,
	excludeCurrentUser,
	signalToken
) {
	return (dispatch) => {
		/*
            Searches the users.

            @param {String} query - Search paramter for user name

            @param {Array} fieldIds - A list of field ids the database should
            search on

            @param {String} letter - Get list of user's with name that starts
            with this letter

            @param {Integer} page - Get page number

            @param {Boolean} limitSelected - if true, it will only show users
            included in the selection object

            @param {String} userSelectionObjectId - uses a selection object
            to search

            @param {String} permissionLevel - filter by permssion level

            @param {String} signedIn - filter based on sign in history

            @param {Bool} activated - filter based on their activation status
        */

		const queryObject = {
			...otherProps,
		};

		if (query.length > 0) {
			queryObject.q = query;
		}

		if (fieldIds.length > 0) {
			queryObject.fieldIds = fieldIds;
		}

		if (letter.length > 0) {
			queryObject.letter = letter;
		}

		// limit_selected can only be set if we have a selection object id
		if (limitSelected && userSelectionObjectId) {
			queryObject.limit_selected = limitSelected;
		}

		if (exclusionList) {
			queryObject.exclusionList = exclusionList;
		}

		if (usersToExcludeSelectionId) {
			queryObject.usersToExcludeSelectionId = usersToExcludeSelectionId;
		}

		if (excludeCurrentUser) {
			queryObject.excludeCurrentUser = excludeCurrentUser;
		}

		queryObject.page = page;
		queryObject.last_name = lastName;

		// generate the query string
		const queryString = SerializeObject(queryObject);

		if (userSelectionObjectId) {
			dispatch({
				type: `${baseAction}:SEARCH_SELECTION`,
				queryString,
				query,
				letter,
				page,
				userSelectionObjectId,
				exclusionList,
				usersToExcludeSelectionId,
				excludeCurrentUser,
			});
		} else {
			dispatch({
				type: `${baseAction}:SEARCH`,
				queryString,
				query,
				letter,
				page,
				signalToken,
			});
		}
	};
}

export function GetUser(userId) {
	return (dispatch) => {
		dispatch({ type: `${baseAction}:GETONE`, userId });
	};
}

export function ParseUserSearchResponse(users, userCounts, secondaryUserDataMap, pageCount, company_billing_admins) {
	return (dispatch) => {
		/*

            I should probably do some composition and break this up into
            several functions because this is doing a lot of different
            things.

            users is a list user objects.

            counts a list of counts for alphabetical pagination.

            secondaryUserDataMap is a map of other users that are involved
            in the 'users' list. for example, the field 'manager' references
            a person, so the user it references is added to this map.

            pageCount number of pages available in current query

        */

		// set the order to display the users in
		const userDirectoryOrder = users.map((u) => u._id);

		// parse user label counts
		const userLabelCounts = DirectoryAlphaCounts(userCounts);

		// generate a user map
		const userMap = {};

		// cache which field id is the 'email' field id
		let emailFieldId = null;

		users.forEach((userObject) => {
			const dataFields = {};
			const adminFields = {};

			Object.keys(userObject.data_fields).forEach((fieldId) => {
				const fieldName = userObject.data_fields[fieldId].field_name;

				if (!emailFieldId && fieldName === "Email") {
					emailFieldId = fieldId;
				}

				dataFields[fieldId] = {
					value: userObject.data_fields[fieldId].value,
					fieldName,
				};
			});

			if (Object.prototype.hasOwnProperty.call(userObject, "admin_fields")) {
				Object.keys(userObject.admin_fields).forEach((fieldId) => {
					const camelCasedField = Camelfy(fieldId);
					let fieldValue = userObject.admin_fields[fieldId];

					if (camelCasedField === "lastSeen") {
						fieldValue = moment.isMoment(Momentize(fieldValue)) ? Momentize(fieldValue) : fieldValue;
					}

					adminFields[camelCasedField] = fieldValue;
				});
			}

			// set the value in the userMap
			userMap[userObject._id] = new UserObjectRecord({
				_id: userObject._id,
				name: userObject.name,
				hris_locked: userObject.hris_locked,
				dataFields: fromJS(userObject.data_fields),
				adminFields: fromJS(adminFields),
			});
		});

		/*
            add the users from the secondaryUserDataMap into the userMap if
            they aren't already listed
        */
		Object.keys(secondaryUserDataMap).forEach((userId) => {
			if (!Object.prototype.hasOwnProperty.call(userMap, userId)) {
				const secondaryUser = secondaryUserDataMap[userId];
				const name = secondaryUser.active ? secondaryUser.name : "";
				userMap[userId] = new UserObjectRecord({ _id: userId, name });
			}
		});

		// send to the store
		dispatch({ type: "FIELDS:SET_EMAIL_FIELD", emailFieldId });
		dispatch({
			type: `${baseAction}:PARSE_SEARCH`,
			userMap: fromJS(userMap),
			userDirectoryOrder: fromJS(userDirectoryOrder),
			userCounts: fromJS(userLabelCounts),
			pageCount,
			companyBillingAdmins: company_billing_admins,
		});
	};
}

export function ParseUsersResponse(response) {
	return (dispatch) => {
		dispatch({ type: `${baseAction}:PARSE_ALL`, data: response });
	};
}

export function ParseUserResponse(data) {
	return (dispatch) => {
		dispatch({
			type: `${baseAction}:PARSE_ONE`,
			userId: data.id,
			user: data,
		});
	};
}

export function RedoLastSearch() {
	return (dispatch, getState) => {
		/*
            We store the last search in the users.reducer. this just calls it.
        */

		const currentState = getState();
		const lastSearch = currentState.users.get("lastSearch");

		dispatch(lastSearch);
	};
}

export function PendingUserUpdate() {
	return (dispatch) => {
		dispatch({
			type: `${baseAction}:REMOVE_PENDING`,
		});
	};
}

export function CancelUserUpdate() {
	return (dispatch) => {
		dispatch({
			type: `${baseAction}:REMOVE_COMPLETE`,
		});
	};
}
